import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useFirestore from "../hooks/useFirestore";
import "./Settings.css";

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const { artworks } = useFirestore();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const exportToJSON = () => {
    const dataStr = JSON.stringify(artworks, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const exportFileDefaultName = `sicilia-artworks-${
      new Date().toISOString().split("T")[0]
    }.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  const exportToCSV = () => {
    if (artworks.length === 0) return;

    const headers = [
      "Title",
      "Artist",
      "Location",
      "Institution",
      "Contact",
      "Status",
      "Notes",
      "Created At",
    ];
    const csvContent = [
      headers.join(","),
      ...artworks.map((artwork) =>
        [
          `"${artwork.title}"`,
          `"${artwork.artist}"`,
          `"${artwork.location}"`,
          `"${artwork.institution}"`,
          `"${artwork.contact}"`,
          `"${artwork.status}"`,
          `"${artwork.notes || ""}"`,
          `"${new Date(artwork.createdAt).toLocaleDateString()}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `sicilia-artworks-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.click();
  };

  // Statistiche dell'account
  const accountStats = {
    totalArtworks: artworks.length,
    pending: artworks.filter((a) => a.status === "pending").length,
    inProgress: artworks.filter((a) => a.status === "in-progress").length,
    obtained: artworks.filter((a) => a.status === "obtained").length,
    digitized: artworks.filter((a) => a.isDigitized).length,
    joinDate: user?.metadata?.creationTime
      ? new Date(user.metadata.creationTime).toLocaleDateString()
      : "Unknown",
    lastLogin: user?.metadata?.lastSignInTime
      ? new Date(user.metadata.lastSignInTime).toLocaleDateString()
      : "Unknown",
  };

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="settings-header">
        {/* Decorative elements */}
        <div className="settings-header-decoration-1"></div>
        <div className="settings-header-decoration-2"></div>

        <div className="settings-header-content">
          <div className="settings-header-title-section">
            <span className="settings-header-icon">⚙️</span>
            <div className="settings-header-title-content">
              <h1 className="settings-header-title">Account Settings</h1>
              <p className="settings-header-subtitle">
                Manage your account and preferences
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="settings-section">
        <h2 className="settings-section-title">
          <span className="settings-section-icon">👤</span>
          Account Information
        </h2>

        <div className="settings-account-info-grid">
          {/* User Profile Card */}
          <div className="settings-user-profile-card">
            <div className="settings-user-header">
              <div className="settings-user-avatar">
                {user?.email?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="settings-user-info">
                <h3 className="settings-user-name">
                  {user?.email?.split("@")[0] || "User"}
                </h3>
                <p className="settings-user-email">{user?.email}</p>
              </div>
            </div>

            <div className="settings-user-details">
              <div className="settings-user-detail-item">
                <span className="settings-user-detail-label">
                  📅 Member since:
                </span>
                <span className="settings-user-detail-value">
                  {accountStats.joinDate}
                </span>
              </div>
              <div className="settings-user-detail-item">
                <span className="settings-user-detail-label">
                  🕐 Last login:
                </span>
                <span className="settings-user-detail-value">
                  {accountStats.lastLogin}
                </span>
              </div>
            </div>
          </div>

          {/* Account Statistics */}
          <div className="settings-account-stats-card">
            <h3 className="settings-stats-header">📊 Collection Statistics</h3>

            <div className="settings-stats-grid">
              <div className="settings-stats-total">
                <span className="settings-stats-total-label">
                  🎨 Total Artworks:
                </span>
                <span className="settings-stats-total-value">
                  {accountStats.totalArtworks}
                </span>
              </div>
              <div className="settings-stats-divider"></div>
              <div className="settings-stats-breakdown">
                <div className="settings-stats-item">
                  <div className="settings-stats-item-value pending">
                    {accountStats.pending}
                  </div>
                  <div className="settings-stats-item-label">📋 Pending</div>
                </div>
                <div className="settings-stats-item">
                  <div className="settings-stats-item-value in-progress">
                    {accountStats.inProgress}
                  </div>
                  <div className="settings-stats-item-label">
                    ⚡ In Progress
                  </div>
                </div>
                <div className="settings-stats-item">
                  <div className="settings-stats-item-value obtained">
                    {accountStats.obtained}
                  </div>
                  <div className="settings-stats-item-label">🎯 Obtained</div>
                </div>
                <div className="settings-stats-item">
                  <div className="settings-stats-item-value completed">
                    {accountStats.digitized}
                  </div>
                  <div className="settings-stats-item-label">✅ Digitized</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="settings-section">
        <h2 className="settings-section-title">
          <span className="settings-section-icon">💾</span>
          Data Management
        </h2>

        <div className="settings-data-management-grid">
          {/* Export Data Card */}
          <div className="settings-export-card">
            <div className="settings-export-header">
              <div className="settings-export-title-section">
                <span className="settings-export-icon">📤</span>
                <h3 className="settings-export-title">Export Your Data</h3>
              </div>
              <p className="settings-export-description">
                Download your artwork collection in different formats for backup
                or sharing purposes.
              </p>
            </div>

            <div className="settings-export-buttons">
              <button
                onClick={exportToJSON}
                disabled={artworks.length === 0}
                className="settings-export-button json"
              >
                📄 JSON
              </button>
              <button
                onClick={exportToCSV}
                disabled={artworks.length === 0}
                className="settings-export-button csv"
              >
                📊 CSV
              </button>
            </div>

            {artworks.length === 0 && (
              <p className="settings-export-warning">
                ⚠️ No artworks to export. Add some artworks first!
              </p>
            )}
          </div>

          {/* Backup Info Card */}
          <div className="settings-backup-card">
            <div className="settings-backup-header">
              <div className="settings-backup-title-section">
                <span className="settings-backup-icon">🔐</span>
                <h3 className="settings-backup-title">Data Backup</h3>
              </div>
              <p className="settings-backup-description">
                Your data is automatically backed up in real-time to Firebase.
                Regular exports are recommended for additional security.
              </p>
            </div>

            <div className="settings-backup-tip">
              <div className="settings-backup-tip-header">
                <span className="settings-backup-tip-icon">💡</span>
                <strong className="settings-backup-tip-title">Pro Tip:</strong>
              </div>
              <p className="settings-backup-tip-text">
                Export your data monthly to keep local backups of your growing
                collection.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Actions */}
      <div className="settings-section">
        <h2 className="settings-section-title">
          <span className="settings-section-icon">🔧</span>
          Account Actions
        </h2>

        <div className="settings-account-actions-content">
          <div className="settings-logout-header">
            <div className="settings-logout-title-section">
              <span className="settings-logout-icon">�</span>
              <h3 className="settings-logout-title">Sign Out</h3>
            </div>
            <p className="settings-logout-description">
              Sign out of your account. You'll need to log in again to access
              your artwork collection.
            </p>
          </div>

          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="settings-logout-button"
          >
            🚪 Sign Out
          </button>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="settings-modal-overlay">
          <div className="settings-modal-content">
            <div className="settings-modal-header">
              <div className="settings-modal-icon">🚪</div>
              <h3 className="settings-modal-title">Confirm Sign Out</h3>
              <p className="settings-modal-description">
                Are you sure you want to sign out of your account?
              </p>
            </div>

            <div className="settings-modal-actions">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="settings-modal-button cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setShowLogoutConfirm(false);
                }}
                className="settings-modal-button confirm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
